'use client';

import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeRefs } from '@react-aria/utils';
import { cloneElement, useRef, type ReactElement, type ReactNode } from 'react';

import {
  type Elevation,
  elevationStyles,
} from '../../../internal/styles/elevation';
import { filterProps } from '../../../internal/utils/filterProps';
import { focusRingStyles } from '../../../styles/focusRing';
import { type Component } from '../../../types/component';
import { type ResponsiveOption } from '../../../types/responsiveOption';
import { mergeProps } from '../../../utils/mergeProps';
import { resolveResponsiveProps } from '../../../utils/resolveResponsiveProps';
import { tv } from '../../../utils/tv';
import { Typography } from '../../typography/Typography';
import { chipClasses } from '../chipClasses';

// Tailwind v4 has no arbitrary ring-width syntax — `ring-[...]` resolves as a colour, not a width.
// Override `--tw-ring-shadow` directly to use border-width tokens while keeping Tailwind's
// box-shadow composition (elevation via `--tw-shadow` continues to stack correctly).
const ringBorderDeselectedClass =
  '[--tw-ring-shadow:inset_0_0_0_var(--border-sm)_var(--tw-ring-color,currentcolor)]';
const ringBorderSelectedClass =
  '[--tw-ring-shadow:inset_0_0_0_var(--border-md)_var(--tw-ring-color,currentcolor)]';

const chipStyles = tv({
  slots: {
    root: [
      chipClasses.root,
      'appearance-none',
      'p-0',
      'border-none',
      'inline-flex',
      'items-center',
      'rounded-full',
      'gap-1.5',
      'pr-3',
      'cursor-pointer',
      'max-w-full',
      `ring ring-neutral-main ${ringBorderDeselectedClass}`,
    ],
    icon: [chipClasses.icon, 'shrink-0'],
    avatar: [chipClasses.avatar, 'shrink-0'],
    label: [chipClasses.label, 'inline-flex', 'items-center', 'gap-1.5'],
    trailing: [chipClasses.trailing, 'w-4', 'h-4', 'shrink-0'],
  },
  variants: {
    size: {
      sm: { root: 'z--chip-sm-height' },
      md: { root: 'z--chip-md-height' },
    },
    sizeXs: {
      sm: { root: 'xs:z--chip-sm-height' },
      md: { root: 'xs:z--chip-md-height' },
    },
    sizeSm: {
      sm: { root: 'sm:z--chip-sm-height' },
      md: { root: 'sm:z--chip-md-height' },
    },
    sizeMd: {
      sm: { root: 'md:z--chip-sm-height' },
      md: { root: 'md:z--chip-md-height' },
    },
    sizeLg: {
      sm: { root: 'lg:z--chip-sm-height' },
      md: { root: 'lg:z--chip-md-height' },
    },
    sizeXl: {
      sm: { root: 'xl:z--chip-sm-height' },
      md: { root: 'xl:z--chip-md-height' },
    },
    isFullWidth: {
      true: { root: 'w-full justify-center' },
    },
    icon: {
      true: {},
      false: {},
    },
    avatar: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        root: `cursor-not-allowed disabled-background disabled-foreground [--tw-ring-color:var(--disabled-border)] ${ringBorderDeselectedClass}`,
        icon: 'opacity-disabled [&>*]:grayscale',
        avatar: 'opacity-disabled [&>*]:grayscale',
      },
    },
    isSelected: {
      true: {},
      false: {},
    },
    elevation: {
      1: {},
      2: {},
      3: {},
    },
  },
  compoundVariants: [
    { icon: false, avatar: false, class: { root: 'pl-3' } },
    { icon: true, class: { root: 'pl-3' } },
    { avatar: true, class: { root: 'pl-1.5' } },
    {
      isSelected: false,
      className: {
        root: 'hover:not-disabled:bg-neutral-muted-5-blank-hovered pressed:not-disabled:bg-neutral-muted-5-blank-pressed',
      },
    },
    {
      isSelected: false,
      elevation: [1, 2, 3] as const,
      className: {
        root: 'hover:not-disabled:bg-neutral-muted-5-blank-hovered-solid pressed:not-disabled:bg-neutral-muted-5-blank-pressed-solid',
      },
    },
    {
      isSelected: true,
      isDisabled: false,
      className: {
        root: [
          `ring ring-accent-primary-main ${ringBorderSelectedClass}`,
          'text-accent-primary-main',
          'bg-accent-primary-main-blank-hovered',
        ],
      },
    },
    {
      isSelected: true,
      isDisabled: false,
      elevation: [1, 2, 3] as const,
      className: {
        root: 'bg-accent-primary-main-blank-hovered-solid',
      },
    },
  ],
});

interface ChipBaseSharedProps {
  /**
   * The label content of the chip.
   */
  children: ReactNode;
  /**
   * Node rendered in the trailing slot (dismiss button, chevron, etc.).
   */
  trailing?: ReactNode;
  /**
   * The size of the chip. Accepts a scalar value or a responsive object.
   * @default 'md'
   * @example 'sm'
   * @example { default: 'sm', lg: 'md' }
   */
  size?: ResponsiveOption<'sm' | 'md'>;
  /**
   * Whether the chip is disabled.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Visual elevation that lifts the chip off the background — use on complex surfaces such as images or maps. Higher values lift further; omit for none.
   */
  elevation?: Elevation;
  /**
   * Whether the chip appears in a selected state.
   * @default false
   */
  isSelected?: boolean;
  /**
   * Whether the chip stretches to fill its container's width.
   * @default false
   */
  isFullWidth?: boolean;
}

interface ChipBaseWithIcon extends ChipBaseSharedProps {
  /**
   * Icon element displayed before the label. Must be a Zeus `<Icon>` — do not set `size` on the element, the chip controls sizing automatically.
   */
  icon?: ReactElement<{ size: string; className?: string }>;
  avatar?: never;
}

interface ChipBaseWithAvatar extends ChipBaseSharedProps {
  /**
   * Avatar element displayed before the label. Must be a Zeus `<Avatar>` — do not set `size` on the element, the chip controls sizing automatically.
   */
  avatar?: ReactElement<{ size: string; className?: string }>;
  icon?: never;
}

type ChipBaseBaseProps = ChipBaseWithIcon | ChipBaseWithAvatar;

type ChipBaseComponent = Component<ChipBaseBaseProps, 'button', 'disabled'>;

const ChipLeading = ({
  element,
  slotFn,
  defaultSize,
}: {
  element: ReactElement<{ size: string; className?: string }>;
  slotFn: (opts: { class?: string }) => string;
  defaultSize: string;
}) =>
  cloneElement(element, {
    size: defaultSize,
    className: slotFn({ class: element.props.className }),
  });

const ChipBase: ChipBaseComponent = (props) => {
  const [localProps, buttonRaProps] = filterProps(props, ['useButton']);

  const {
    children,
    icon,
    avatar,
    trailing,
    size: sizeProp,
    elevation,
    isSelected,
    isFullWidth,
    className,
    ref,
    ...restLocalProps
  } = localProps;

  // Pull controlled ARIA and isDisabled from buttonRaProps — these must either
  // win last-write (aria-pressed, aria-label) or be available before useButton.
  const {
    'aria-pressed': ariaPressed,
    'aria-label': ariaLabel,
    isDisabled: isDisabledFromRa,
    ...restButtonRa
  } = buttonRaProps;

  const isDisabled = isDisabledFromRa ?? false;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const { buttonProps, isPressed } = useButton(
    {
      ...restButtonRa,
      isDisabled,
      type: restButtonRa.type ?? 'button',
      elementType: 'button',
    },
    buttonRef
  );

  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { focusProps, isFocusVisible } = useFocusRing();

  const focusStyles = focusRingStyles();

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const resolvedSize = defaultSize ?? 'md';

  const styles = chipStyles({
    size: resolvedSize,
    sizeXs: responsiveSize?.xs,
    sizeSm: responsiveSize?.sm,
    sizeMd: responsiveSize?.md,
    sizeLg: responsiveSize?.lg,
    sizeXl: responsiveSize?.xl,
    icon: icon != null,
    avatar: avatar != null,
    isDisabled,
    isSelected: isSelected ?? false,
    isFullWidth: isFullWidth ?? false,
    elevation,
  });

  const rootStyles = focusStyles.root({
    className: elevationStyles({
      elevation,
      withSurface: true,
      className: styles.root({ className }),
    }),
  });

  return (
    <button
      {...mergeProps(restLocalProps, buttonProps, hoverProps, focusProps)}
      ref={mergeRefs(buttonRef, ref)}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-disabled={isDisabled || undefined}
      data-pressed={isPressed || undefined}
      aria-pressed={ariaPressed}
      aria-label={ariaLabel}
      className={rootStyles}
    >
      {icon != null && (
        <ChipLeading element={icon} slotFn={styles.icon} defaultSize="md" />
      )}
      {avatar != null && (
        <ChipLeading element={avatar} slotFn={styles.avatar} defaultSize="sm" />
      )}
      <Typography
        component="span"
        variant="body2"
        color="inherit"
        className={styles.label()}
      >
        {children}
      </Typography>
      {trailing != null && (
        <span aria-hidden="true" className={styles.trailing()}>
          {trailing}
        </span>
      )}
    </button>
  );
};

ChipBase.displayName = 'ChipBase';

export { ChipBase, type ChipBaseWithIcon, type ChipBaseWithAvatar };
